import {
  createPlannedMatch,
  patchPlannedMatch,
  deletePlannedMatchById,
  deletePlannedMatchesForBookings,
} from '../lib/api/matches';

// ─────────────────────────────────────────────────────────────────────
// usePlannedMatchActions — CRUD for "planned" (not-yet-played) single
// matches created from a court booking. A planned match holds only the
// INPUT a single match needs (team1/team2/eloWeightPct) plus the linking
// `bookingId` — NO sets/result yet. It is stored separately in
// 'planned_matches_v1' so the result-bearing single_matches_v1 consumers
// (useDerivations.liveAllPlayers etc.) are never fed a result-less record.
//
// When the match is actually played, `completePlannedMatch` routes the
// captured score through the EXISTING `addSingleMatch` pipeline (which owns
// the ELO calc + single_matches_v1 + players_v1 writes) and then removes
// the plan. We deliberately do NOT depend on useSingleMatchActions here —
// `addSingleMatch` is injected at call time so this hook stays self-contained
// and unit-testable.
//
// Each mutator is persist-first (await the write before setState), per the
// HANDOVER §13 ordering rule.
//
// SINCE #245 EACH SENDS AN INTENT, not a document. The whole-collection PUT is
// gone: it upserted from a list the client had read earlier, so a pad that
// deleted a plan when it finalised an enkeltmatch had that plan PUT BACK by the
// next portal save — and the match became finalisable a second time. The server
// now touches only the rows the intent names, and mints the id on create.
//
// On the in-memory backend these fall back to the read-modify-write the app has
// always done, unchanged — that is what the deployed browser still runs. See
// apps/web/src/lib/api/matches.ts.
// ─────────────────────────────────────────────────────────────────────
export interface UsePlannedMatchActionsDeps {
  plannedMatches: any[];
  setPlannedMatches: (v: any) => void;
}

export function usePlannedMatchActions({
  plannedMatches,
  setPlannedMatches,
}: UsePlannedMatchActionsDeps) {
  // Create (or replace) the plan for a booking. One plan per booking — adding
  // a second plan for the same bookingId replaces the first (no duplicates).
  const addPlannedMatch = async ({
    bookingId,
    team1,
    team2,
    eloWeightPct,
    matchFormat,
    deuceMode,
    // Optional level gate for open slots (mobile «Matcher»): members outside
    // [levelMin, levelMax] cannot join. Additive — absent means no gate.
    levelMin,
    levelMax,
  }: any) => {
    if (!bookingId || !team1 || !team2) {
      return { success: false, error: 'Mangler bookingId eller lag' };
    }
    const record = {
      id: `pm_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      bookingId,
      team1: { p1Id: team1.p1Id, p2Id: team1.p2Id },
      team2: { p1Id: team2.p1Id, p2Id: team2.p2Id },
      eloWeightPct,
      // Match rules (defaults for live scoring; changeable on Banepad).
      matchFormat: matchFormat || 'bo3_super',
      deuceMode: deuceMode || 'advantage',
      status: 'planned',
      createdAt: Date.now(),
      createdBy: 'user',
      ...(levelMin != null ? { levelMin: Number(levelMin) } : {}),
      ...(levelMax != null ? { levelMax: Number(levelMax) } : {}),
    };
    try {
      // The SERVER replaces any plan this booking already had, in one
      // transaction, and mints the id on the API path (#245). Local state is
      // rebuilt from what it returns rather than from what we guessed.
      const saved = await createPlannedMatch(record);
      const newList = [saved, ...plannedMatches.filter((p) => p.bookingId !== bookingId)];
      setPlannedMatches(newList);
      return { success: true, planned: saved };
    } catch (e: any) {
      console.error('[Padel24] Kunne ikke lagre planlagt kamp:', e);
      return { success: false, error: e?.message || 'ukjent feil' };
    }
  };

  // Change the four players on a plan (the booker editing the line-up before the
  // match starts). Storage-first. Only team1/team2 change — id/booking/rules stay.
  const updatePlannedMatchPlayers = async (id: any, { team1, team2 }: any) => {
    if (!team1 || !team2) return { success: false, error: 'Mangler lag' };
    const teams = {
      team1: { p1Id: team1.p1Id, p2Id: team1.p2Id },
      team2: { p1Id: team2.p1Id, p2Id: team2.p2Id },
    };
    const newList = plannedMatches.map((p) => (p.id === id ? { ...p, ...teams } : p));
    try {
      await patchPlannedMatch(id, teams);
      setPlannedMatches(newList);
      return { success: true };
    } catch (e: any) {
      return { success: false, error: e?.message || 'ukjent feil' };
    }
  };

  // Persist the live (in-progress) score on a plan — continuous banepad scoring.
  // Storage-first; no ELO (that happens on completePlannedMatch).
  const updatePlannedMatchLiveScore = async (id: any, liveScore: any) => {
    const newList = plannedMatches.map((p) => (p.id === id ? { ...p, liveScore } : p));
    try {
      await patchPlannedMatch(id, { liveScore });
      setPlannedMatches(newList);
      return { success: true };
    } catch (e: any) {
      return { success: false, error: e?.message || 'ukjent feil' };
    }
  };

  const deletePlannedMatch = async (id: any) => {
    const newList = plannedMatches.filter((p) => p.id !== id);
    if (newList.length === plannedMatches.length) return { success: true }; // nothing to remove
    try {
      await deletePlannedMatchById(id);
      setPlannedMatches(newList);
      return { success: true };
    } catch (e: any) {
      return { success: false, error: e?.message || 'ukjent feil' };
    }
  };

  // Remove every plan linked to the given booking id(s). Accepts a single id,
  // an array, or a Set. Used as a cascade when a booking is deleted.
  const deletePlannedMatchesByBookingId = async (bookingIdOrIds: any) => {
    const ids =
      bookingIdOrIds instanceof Set
        ? bookingIdOrIds
        : new Set(Array.isArray(bookingIdOrIds) ? bookingIdOrIds : [bookingIdOrIds]);
    const newList = plannedMatches.filter((p) => !ids.has(p.bookingId));
    if (newList.length === plannedMatches.length) return { success: true }; // nothing to remove
    try {
      await deletePlannedMatchesForBookings([...ids].map(String));
      setPlannedMatches(newList);
      return { success: true };
    } catch (e: any) {
      return { success: false, error: e?.message || 'ukjent feil' };
    }
  };

  // Enter the result for a planned match: run the captured score through the
  // existing single-match pipeline (ELO + persistence), then drop the plan.
  // `addSingleMatch` is injected (from useSingleMatchActions). The plan seeds the
  // teams/eloWeight, but the caller's `data` (from the result-entry modal) wins
  // when present — so a late player swap or weight tweak is respected. On any
  // failure the plan is left intact so the user can retry.
  const completePlannedMatch = async (
    id: any,
    data: any,
    addSingleMatch: (args: any) => Promise<any>,
  ) => {
    const plan = plannedMatches.find((p) => p.id === id);
    if (!plan) return { success: false, error: 'Planlagt kamp ble ikke funnet' };

    const res = await addSingleMatch({
      team1: data?.team1 || plan.team1,
      team2: data?.team2 || plan.team2,
      sets: data?.sets,
      // Carry the match rules from the plan (the booking-time defaults).
      format: plan.matchFormat || data?.format || 'bo3_super',
      deuceMode: plan.deuceMode || 'advantage',
      eloWeightPct: data?.eloWeightPct != null ? data.eloWeightPct : plan.eloWeightPct,
      playedAt: data?.playedAt,
    });
    if (!res || !res.success) return res || { success: false, error: 'ukjent feil' };

    await deletePlannedMatch(id);
    return res;
  };

  return {
    addPlannedMatch,
    updatePlannedMatchPlayers,
    updatePlannedMatchLiveScore,
    deletePlannedMatch,
    deletePlannedMatchesByBookingId,
    completePlannedMatch,
  };
}
